// The HTTP client of the contract in types.ts. Every failure reaches the
// caller as an ApiError: the server's `{error: <code>, message, reason?}` when
// it sent one (a 410 `snapshot_needed` and a 401 among them), or what went
// wrong on the way. The browser sends the session's cookie with each request
// to the page's own origin, and the `Origin` header with each POST, which the
// server checks. A request made as a member (`as`, types.ts's Api) names it
// in `Poulpe-As`, which the server checks against the session or the token.
import type { PlanLimitsReply } from './planLimits'
import type { ProgramTokens, NewProgramToken, WorkDigest, Api, ChangesPage, Confirmed, CreationOptions, EmailStarted, ErrorBody, Me, PasskeyList, Mutation, MutationResult, IssueRevision, IssueRevisions, PlanDetail, ProposalChanges, SearchAnswer, SearchOptions, ProposalChangesDiff, RequestOptions, Runner, RunnerCode, SessionChangesDiff, SignUpStatus, Snapshot, Started, TranscriptPage } from './types'

export class ApiError extends Error {
  /** The HTTP status; 0 when the server was not reached. */
  readonly status: number
  readonly code: string
  /** The rule's reason, when a rule refused: `stale`, `not_the_target`… */
  readonly reason: string | undefined
  readonly detail: unknown

  constructor(status: number, code: string, message: string, reason?: string, detail?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.reason = reason
    this.detail = detail
  }
}

/** The header that names the member a request acts as. */
export const ACTING_AS = 'Poulpe-As'

/** Where the web app's failures are reported (Poulpe-706). */
export const WEB_REPORTS = '/v1/web-reports'

/** A request that failed: a 5xx, or no answer (status 0). */
export interface RequestFailure {
  method: string
  /** Its path, without its query. */
  path: string
  status: number
  /** The `x-request-id` the server answered with, if any. */
  requestId: string | null
}

export interface HttpApiOptions {
  /** Where the server is; '' for the page's own origin. */
  baseUrl: string
  /** The bearer token, read at each request; null sends none, and the session's cookie signs the request in. */
  token: () => string | null
  fetch?: typeof globalThis.fetch
  timeoutMs?: number
  /** Told of each request that fails with a 5xx or no answer; never of a failed report. */
  onFailure?: (failure: RequestFailure) => void
}

/** What a response that is not ok says: the server's error when it sent one. */
async function errorFrom(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '')
  try {
    const body = JSON.parse(text) as (Partial<Record<keyof ErrorBody, unknown>> & Record<string, unknown>) | null
    if (typeof body?.error === 'string' && typeof body.message === 'string') {
      const detail = body.detail ?? (typeof body.field === 'string' && typeof body.current === 'string' && typeof body.incoming === 'string'
        ? { field: body.field, current: body.current, incoming: body.incoming } : undefined)
      return new ApiError(res.status, body.error, body.message, typeof body.reason === 'string' ? body.reason : undefined, detail)
    }
  } catch {
    // Not JSON: a proxy's page or a crash.
  }
  const detail = text.trim().replace(/\s+/g, ' ').slice(0, 200)
  return new ApiError(res.status, `http_${res.status}`, detail || res.statusText || `HTTP ${res.status}`)
}

export function httpApi({ baseUrl, token, fetch = globalThis.fetch.bind(globalThis), timeoutMs = 20_000, onFailure }: HttpApiOptions): Api {
  function failed(method: string, path: string, status: number, requestId: string | null) {
    const bare = path.split(/[?#]/, 1)[0]!
    if (!onFailure || bare === WEB_REPORTS) return
    try {
      onFailure({ method, path: bare, status, requestId })
    } catch {
      // A report must never break the request it reports.
    }
  }

  async function request<T>(method: 'GET' | 'POST', path: string, body?: unknown, as?: string | null, blob = false, deadlineMs = timeoutMs): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    const bearer = token()
    if (bearer) headers.Authorization = `Bearer ${bearer}`
    if (as) headers[ACTING_AS] = as
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    let res: Response
    try {
      res = await fetch(`${baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'same-origin',
        signal: AbortSignal.timeout(deadlineMs),
      })
    } catch (err) {
      failed(method, path, 0, null)
      if (err instanceof DOMException && err.name === 'TimeoutError') {
        throw new ApiError(0, 'timeout', 'The board server did not answer in time.')
      }
      throw new ApiError(0, 'network', 'The board server could not be reached.')
    }
    if (!res.ok) {
      if (res.status >= 500) failed(method, path, res.status, res.headers.get('x-request-id'))
      throw await errorFrom(res)
    }
    if (blob) return await res.blob() as T
    if (res.status === 204) return undefined as T
    try {
      return (await res.json()) as T
    } catch {
      throw new ApiError(res.status, 'bad_response', 'The board server sent a response that could not be read.')
    }
  }

  return {
    repoDocs: (project, as) => request('GET', `/v1/projects/${encodeURIComponent(project)}/repo-docs`, undefined, as),
    repoDoc: (project, path, sha, as) => request('GET', `/v1/projects/${encodeURIComponent(project)}/repo-docs/${path.split('/').map(encodeURIComponent).join('/')}?sha=${encodeURIComponent(sha)}`, undefined, as),
    programTokens: (as) => request<ProgramTokens>('GET', '/v1/program-tokens', undefined, as),
    createProgramToken: (args, as) => request<NewProgramToken>('POST', '/v1/program-tokens', args, as),
    revokeProgramToken: (id, as) => request<{ revoked: string }>('POST', `/v1/program-tokens/${encodeURIComponent(id)}/revoke`, undefined, as),
    github: (project, as) => request('GET', `/v1/projects/${encodeURIComponent(project)}/github`, undefined, as),
    refreshGitHub: (project, as) => request('POST', `/v1/projects/${encodeURIComponent(project)}/github`, undefined, as),
    pushStatus: (as) => request('GET', '/v1/push', undefined, as),
    registerPushDevice: (args, as) => request('POST', '/v1/push/devices', args, as),
    removePushDevice: (id, as) => request('POST', `/v1/push/devices/${encodeURIComponent(id)}/remove`, undefined, as),
    testPush: (as) => request('POST', '/v1/push/test', undefined, as),
    pushPresence: (here, as) => request('POST', '/v1/push/presence', { here }, as),
    registerPhone: (args, as) => request('POST', '/v1/push/native', args, as),
    forgetPhone: (as) => request('POST', '/v1/push/native/remove', undefined, as),
    reportError: (report) => request('POST', WEB_REPORTS, report),
    promptFile: (id, as) => request<Blob>('GET', `/v1/prompt-files/${encodeURIComponent(id)}`, undefined, as, true),
    attachmentFile: (id, as) => request<Blob>('GET', `/v1/${/^[0-9a-f]{64}$/.test(id) ? 'files' : 'attachments'}/${encodeURIComponent(id)}`, undefined, as, true),
    digest: (project, options = {}, as) => {
      const params = new URLSearchParams({ project })
      if (options.since !== undefined) params.set('since', options.since)
      if (options.limit !== undefined) params.set('limit', String(options.limit))
      return request<WorkDigest>('GET', `/v1/digest?${params}`, undefined, as)
    },
    snapshot: (as) => request<Snapshot>('GET', '/v1/snapshot', undefined, as),
    changes: (since, limit, as, epoch) =>
      request<ChangesPage>('GET', `/v1/changes?since=${since}&limit=${limit}${epoch ? `&epoch=${encodeURIComponent(epoch)}` : ''}`, undefined, as),
    mutate: (mutation: Mutation, as) => request<MutationResult>('POST', '/v1/mutations', mutation, as),
    // Never as anyone: it reads who is signed in.
    me: () => request<Me>('GET', '/v1/me'),
    setProfile: (change, as) => request<Me>('POST', '/v1/me', change, as),
    signUpStart: (args) => request<Started<CreationOptions>>('POST', '/v1/auth/signup/start', args),
    signUpFinish: (finish) => request<Me>('POST', '/v1/auth/signup/finish', finish),
    signUpStatus: () => request<SignUpStatus>('POST', '/v1/auth/signup/status'),
    signInStart: () => request<Started<RequestOptions>>('POST', '/v1/auth/signin/start'),
    signInFinish: (finish) => request<Me>('POST', '/v1/auth/signin/finish', finish),
    signOut: () => request<void>('POST', '/v1/auth/signout'),
    confirmStart: (action, as) => request<Started<RequestOptions>>('POST', '/v1/auth/confirm/start', { action }, as),
    confirmFinish: (finish, as) => request<Confirmed>('POST', '/v1/auth/confirm/finish', finish, as),
    setConfirmations: (confirm, as) => request<Me>('POST', '/v1/auth/confirmations', { confirm_actions: confirm }, as),
    emailStart: (args) => request<EmailStarted>('POST', '/v1/auth/email/start', args),
    emailAddStart: (args, as) => request<EmailStarted>('POST', '/v1/auth/email/add/start', args, as),
    emailFinish: (args) => request<Me>('POST', '/v1/auth/email/finish', args),
    passkeys: (as) => request<PasskeyList>('GET', '/v1/auth/passkeys', undefined, as),
    passkeyAddStart: (as) => request<Started<CreationOptions>>('POST', '/v1/auth/passkeys/add/start', undefined, as),
    passkeyAddFinish: (finish, as) => request<PasskeyList>('POST', '/v1/auth/passkeys/add/finish', finish, as),
    passkeyName: (id, name, as) => request<PasskeyList>('POST', `/v1/auth/passkeys/${encodeURIComponent(id)}/name`, { name }, as),
    passkeyRemove: (id, as) => request<PasskeyList>('POST', `/v1/auth/passkeys/${encodeURIComponent(id)}/remove`, undefined, as),
    repositorySettings: (runner, query) => request('POST', `/v1/runners/${encodeURIComponent(runner)}/repository-settings`, query, undefined, false, 35_000),
    agentSettings: (runner) => request('GET', `/v1/runners/${encodeURIComponent(runner)}/agent-settings`, undefined, undefined, false, 35_000),
    setAgentSettings: (runner, change) => request('POST', `/v1/runners/${encodeURIComponent(runner)}/agent-settings`, change, undefined, false, 35_000),
    changeCommandRule: (runner, change) => request('POST', `/v1/runners/${encodeURIComponent(runner)}/agent-settings/rules`, change, undefined, false, 35_000),
    runnerSettings: (runner) => request('GET', `/v1/runners/${encodeURIComponent(runner)}/settings`, undefined, undefined, false, 35_000),
    setRunnerSettings: (runner, change) => request('POST', `/v1/runners/${encodeURIComponent(runner)}/settings`, change, undefined, false, 35_000),
    cacheHealth: (runner) => request('GET', `/v1/runners/${encodeURIComponent(runner)}/cache-health`, undefined, undefined, false, 65_000),
    localModels: (runner) => request('GET', `/v1/runners/${encodeURIComponent(runner)}/local-models`, undefined, undefined, false, 35_000),
    localModel: (runner, action) => request('POST', `/v1/runners/${encodeURIComponent(runner)}/local-models`, action, undefined, false, 35_000),
    planLimits: (runner, as) => request<PlanLimitsReply>('GET', `/v1/runners/${encodeURIComponent(runner)}/plan-limits`, undefined, as, false, 45_000),
    checkProgress: (as) => request<import('./types').LiveCheck[]>('GET', '/v1/checks/progress', undefined, as),
    queueDetail: (project, as) => request<import('./types').QueueDetail>('GET', `/v1/projects/${encodeURIComponent(project)}/queue`, undefined, as),
    runners: (as) => request<Runner[]>('GET', '/v1/runners', undefined, as),
    runnerCode: (as) => request<RunnerCode>('POST', '/v1/runners/codes', undefined, as),
    sessionChangesDiff: (session, path, as) => request<SessionChangesDiff>('GET', `/v1/sessions/${encodeURIComponent(session)}/changes/diff?path=${encodeURIComponent(path)}`, undefined, as),
    proposalChanges: (proposal, as) => request<ProposalChanges>('GET', `/v1/proposals/${encodeURIComponent(proposal)}/changes`, undefined, as),
    proposalChangesDiff: (proposal, path, as) => request<ProposalChangesDiff>('GET', `/v1/proposals/${encodeURIComponent(proposal)}/changes/diff?path=${encodeURIComponent(path)}`, undefined, as),
    transcript: (session, after, as) => request<TranscriptPage>('GET', transcriptPath(session, after), undefined, as),
    transcriptBefore: (session, before, as) => request<TranscriptPage>('GET', `/v1/sessions/${encodeURIComponent(session)}/transcript?before=${encodeURIComponent(before)}`, undefined, as),
    issueRevisions: (id, as) => request<IssueRevisions>('GET', `/v1/issues/${encodeURIComponent(id)}/revisions`, undefined, as),
    issueRevision: (id, number, as) => request<IssueRevision>('GET', `/v1/issues/${encodeURIComponent(id)}/revisions/${number}`, undefined, as),
    plan: (id, as) => request<PlanDetail>('GET', `/v1/plans/${encodeURIComponent(id)}`, undefined, as),
    search: (query, options, as) => request<SearchAnswer>('GET', searchPath(query, options), undefined, as),
  }
}

/** Where a search is asked: `/v1/search?q=…`, with the selected project, `all` and the limit when given. */
export function searchPath(query: string, { project, all, limit }: SearchOptions): string {
  const params = new URLSearchParams({ q: query })
  if (project) params.set('project', project)
  if (all) params.set('all', 'true')
  if (limit !== undefined) params.set('limit', String(limit))
  return `/v1/search?${params}`
}

/** Where a session's transcript is read from, after an item or from its start; its socket is the same path. */
export function transcriptPath(session: string, after: string | null): string {
  const path = `/v1/sessions/${encodeURIComponent(session)}/transcript`
  return after === null ? path : `${path}?after=${encodeURIComponent(after)}`
}

/**
 * Whether a failure says the server is away (Poulpe-822): no answer at all
 * (status 0), or a 502, 503 or 504 that is not the server's own error,
 * which the host's proxy answers while the server is stopped. A 500, and the
 * server's own 503, are the server's errors. The agents' relay counts the
 * same answers as offline (`crates/poulpe-runner/src/proxy.rs`).
 */
export function isOffline(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true
  return err.status === 0 || (err.status >= 502 && err.status <= 504 && err.code === `http_${err.status}`)
}

/** What to tell a person of a failure. */
export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}
